import React, { useEffect, useRef } from 'react';
import { Developer } from '../types/developer';
import { formatHourlyRate } from '../utils/airtableUtils';

interface Props {
  developer: Developer | null;
  open: boolean;
  onClose: () => void;
  onSelectDeveloper?: (developer: Developer) => void;
  isSelected?: boolean;
}

const DeveloperDetailsModal: React.FC<Props> = ({ 
  developer, 
  open, 
  onClose, 
  onSelectDeveloper,
  isSelected = false 
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Add error logging
  useEffect(() => {
    if (open && developer) {
      console.log('Modal opened for developer:', {
        id: developer.id,
        name: developer.name,
        bioSummary: developer.bioSummary,
        selectedJobExperienceAI: developer.selectedJobExperienceAI
      });
    }
  }, [open, developer]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  const handleAddToSelection = () => {
    try {
      if (developer && onSelectDeveloper && open && developer.id && developer.name) {
        console.log('Adding developer to selection:', developer.name);
        onSelectDeveloper(developer);
        onClose(); // Close modal after adding to selection
      } else {
        console.warn('Cannot add developer - missing required data:', { developer, onSelectDeveloper, open });
      }
    } catch (error) {
      console.error('Error in handleAddToSelection:', error);
    }
  };

  if (!open || !developer) return null;

  // Additional safety check for developer object
  if (!developer.name || !developer.id) {
    console.warn('Invalid developer object passed to modal:', developer);
    return null;
  }

  // Wrap the entire modal content in error handling
  try {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4">
        <div
          ref={modalRef}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 relative animate-fadeIn"
          tabIndex={-1}
          aria-modal="true"
          role="dialog"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full p-1 transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="flex items-center mb-4 pr-8">
            {developer.photo ? (
              <img
                src={developer.photo}
                alt={developer.name}
                className="w-14 h-14 rounded-full object-cover border-2 border-primary-200 flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-lg font-bold text-primary-700 flex-shrink-0">
                {developer.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>
            )}
            <div className="ml-3 min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate">{developer.name}</h2>
              <div className="inline-block px-2 py-1 mt-1 text-xs font-medium rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                {developer.type}
              </div>
            </div>
          </div>

          {/* Bio Summary - Selling Line */}
          {developer.bioSummary && (
            <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-primary-500 rounded-r-lg">
              <p className="text-gray-700 text-sm italic leading-relaxed">{developer.bioSummary}</p>
            </div>
          )}

          {/* Details Grid */}
          <div className="space-y-3 mb-6">
            {/* Experience & Rate Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Experience:</span>
                <div className="ml-2 flex items-center space-x-2">
                  <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                    {developer.experienceLevel}
                  </span>
                  <span className="text-sm text-gray-500">{developer.yearsExperience}y</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Rate</div>
                <div className="text-lg font-bold text-primary-600">{formatHourlyRate(developer.hourlyRate)}/h</div>
              </div>
            </div>

            {/* Skills */}
            <div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide block mb-2">Skills:</span>
              <div className="flex flex-wrap gap-1">
                {Array.isArray(developer.skills) ? developer.skills.map((skill, index) => (
                  <span 
                    key={index}
                    className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md"
                  >
                    {skill}
                  </span>
                )) : (
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md">
                    {developer.skills}
                  </span>
                )}
              </div>
            </div>

            {/* Selected Experience */}
            {developer.selectedJobExperienceAI && (
              <div>
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide block mb-2">Highlight:</span>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">{developer.selectedJobExperienceAI}</p>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Call-to-Action */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
            <div className="text-center space-y-3">
              {isSelected ? (
                /* Status indicator - only show when selected */
                <div className="flex items-center justify-center space-x-2 text-emerald-600 text-sm font-medium">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Redan tillagd</span>
                </div>
              ) : (
                /* Full CTA flow - only show when not selected */
                <>
                  {/* Main CTA */}
                  <div className="space-y-2">
                    <h3 className="text-gray-900 font-bold text-sm">
                      Skicka mig CV:t
                    </h3>
                  </div>

                  {/* Action Button */}
                  {onSelectDeveloper && (
                    <button
                      onClick={handleAddToSelection}
                      className="w-full px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 focus:ring-primary-500 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Lägg till</span>
                      </div>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering modal for developer:', developer, error);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to load developer details</h3>
            <p className="text-sm text-gray-500 mb-4">There was an issue loading this developer's information.</p>
            <button
              onClick={onClose}
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
};

export default DeveloperDetailsModal; 